import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Typography, Paper, Box, Button, Grid, Chip, IconButton, Modal, Accordion, AccordionSummary, AccordionDetails, List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import * as styles from "../BackofficeRequest/Styles.js";
import * as TextVariables from "../../assets/Variables/TextVariables.js";
import { Spaces, mainBox } from "../../assets/Variables/SpaceVariables.js";
import TableComponent from "../../components/Table/TableComponent.js";
import TableToolTip from "../../components/ToolTips/TableToolTip";
import IconComponent from "../../components/Icons/IconComponent";
import { useCookies } from "../../Tools/CookieChecker.js";
import RequestsRequest from "../../Endpoints/RequestsRequest";
import { SetRequests } from "../../Tools/LocalStorage";
import actionLogo from "../../assets/Icons/open_in_new.svg";
import CryptowalletModal from "./ModalsRequest/CrytowalletModal";
import BankwireModal from "./ModalsRequest/BankwireModal";
import VirtualWalletModal from "./ModalsRequest/VirtualWalletModal";
import BitPayModal from "./ModalsRequest/BitPayModal";
import FasaPayModal from "./ModalsRequest/FasaPayModal";
import UnionpayModal from "./ModalsRequest/UnionpayModal";
import JpConnectModal from "./ModalsRequest/JpConnectModal";
import DealTreeModal from "./ModalsRequest/DealTreeModal";

const dataGrid = [
  {
    title: "Money Request",
    icon: "payments",
    items: [
      {
        title: "Withdrawal request",
        linkTo: "/requests/withdrawal",
        icon: "output_circle",
        filter: "IB/BROKER",
      },
      {
        title: "Transfer to trading account",
        linkTo: "/requests/transfer",
        icon: "swap_horiz",
        filter: "IB/BROKER",
      },
      {
        title: "Transfer between IBs",
        linkTo: "/requests/transfer-ibs",
        icon: "mobile_share_stack",
        filter: "IB",
      },
    ],
  },
  {
    title: "General Request",
    icon: "sync_alt",
    items: [
      {
        title: "MAM Creation",
        linkTo: "/requests/create-mam-account",
        icon: "Markdown",
        filter: "BROKER",
      },
      {
        title: "Deal Tree Request",
        linkTo: "/requests/deal-tree",
        icon: "family_history",
        filter: "IB/BROKER",
      },
      {
        title: "Copier performance fee request",
        linkTo: "/requests/copier-performance-fee",
        icon: "heap_snapshot_multiple",
        filter: "IB/BROKER",
      },
    ],
  },
];

function BackofficePage(props) {
  const navigate = useNavigate();
  const [t] = useTranslation("global");
  const { type, id, currency } = useCookies();
  const [idChat, setIdChat] = useState(null);
  const [rows, setRows] = useState([]);
  const [requestList, setRequestList] = useState([]);
  const [requestInfo, setRequestInfo] = useState({});
  const [viewModal, setViewModal] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (idChat !== null) {
      handleOpenChat(idChat, id);
    }
  }, [idChat]);

  useEffect(() => {
    RequestRequestsListData();
  }, []);

  const RequestRequestsListData = async () => {
    try {
      const response = await RequestsRequest();
      if (response.data != null) {
        let storedRequests = response.data.data.requests;
        setRequestList(response.data.data.requests);

        if (storedRequests && Array.isArray(storedRequests) && storedRequests.length > 0) {
          let actualRows = storedRequests.map((request, index) => {
            let type = "none";
            if (request.comments.length > 0) {
              type = "view";
              if (request.comments[request.comments.length - 1].type !== "USER") {
                type = "pending";
              }
            } else {
              type = "none";
            }

            let row = {
              id: index,
              chat_link: request.id,
              request_type: request.request_type
                .toLowerCase()
                .replace(/_/g, " ")
                .replace(/\b\w/g, (char) => char.toUpperCase()),
              time_elapsed: request.requeted_ago,
              status: request.state,
              view: type,
              chat_history: "",
            };
            return row;
          });

          setRows(actualRows);
          SetRequests(storedRequests);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  let handleViewMore = (params) => {
    setRequestInfo(requestList.find((request) => request.id === params));
    setViewModal(true);
  };

const renderComponent = () => {
  if (!requestInfo?.request_time) {
    return null;
  }

  const date = requestInfo.request_time;
  const datePart = date.split("T")[0];
  const timePart = date.split("T")[1]?.split(".")[0] || "";
  const formattedDatePart = datePart.replace(/-/g, "/");
  const formattedDate = `${formattedDatePart} ${timePart}`;

  const requestType = requestInfo.request_type;
  const withdrawalMethod = requestInfo?.request?.withdrawalMethod;

  if (requestType === "BROKER_WITHDRAW") {
    switch (withdrawalMethod) {
      case "Cryptowallet":
        return <CryptowalletModal requestInfo={requestInfo} formattedDate={formattedDate} />;
      case "Neteller":
      case "Skrill":
      case "Uphold":
      case "Peska":
      case "SticPay":
      case "Advcash":
      case "Interac e-Transfer":
        return <VirtualWalletModal requestInfo={requestInfo} formattedDate={formattedDate} />;
      case "Bank wire transfer":
      case "JPay":
        return <BankwireModal requestInfo={requestInfo} formattedDate={formattedDate} />;
      case "BitPay":
        return <BitPayModal requestInfo={requestInfo} formattedDate={formattedDate} />;
      case "Fasapay":
        return <FasaPayModal requestInfo={requestInfo} formattedDate={formattedDate} />;
      case "UnionPay":
        return <UnionpayModal requestInfo={requestInfo} formattedDate={formattedDate} />;
      case "JpConnect":
        return <JpConnectModal requestInfo={requestInfo} formattedDate={formattedDate} />;
      default:
        return (
          <Box
            sx={{
              backgroundColor: "#EEF2F6",
              borderRadius: "8px",
              padding: "32px 48px",
              display: "flex",
              flexDirection: "column",
              gap: "24px",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <Typography sx={{ fontFamily: TextVariables.Subtitle_4 }}>
              Request details
            </Typography>

            <Typography sx={TextVariables.Body_4}>
              ID:{" "}
              <span style={{ fontFamily: TextVariables.Caption_2_Bold }}>
                {requestInfo.id}
              </span>
            </Typography>

            <Typography sx={TextVariables.Body_4}>
              Request time:{" "}
              <span style={{ fontFamily: TextVariables.Caption_2_Bold }}>
                {formattedDate}
              </span>
            </Typography>

            <Typography sx={TextVariables.Body_4}>
              Request type:{" "}
              <span style={{ fontFamily: TextVariables.Caption_2_Bold }}>
                {requestInfo.request_type?.toLowerCase().replace(/_/g, " ")}
              </span>
            </Typography>

            <Typography sx={TextVariables.Body_4}>
              State:{" "}
              <span style={{ fontFamily: TextVariables.Caption_2_Bold }}>
                {requestInfo.state?.toLowerCase()}
              </span>
            </Typography>
          </Box>
        );
    }
  }
  if (requestType === "IB_COPIER_SIGNAL_PERFORMANCE_FEE") {
    return (
      <Box
        sx={{
          backgroundColor: "#EEF2F6",
          borderRadius: "8px",
          padding: "32px 48px",
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Typography sx={{ fontFamily: TextVariables.Subtitle_4 }}>
          Request details
        </Typography>

        <Typography sx={TextVariables.Body_4}>
          ID:{" "}
          <span style={{ fontFamily: TextVariables.Caption_2_Bold }}>
            {requestInfo.id}
          </span>
        </Typography>

        <Typography sx={TextVariables.Body_4}>
          Request time:{" "}
          <span style={{ fontFamily: TextVariables.Caption_2_Bold }}>
            {formattedDate}
          </span>
        </Typography>

        <Typography sx={TextVariables.Body_4}>
          Request type:{" "}
          <span style={{ fontFamily: TextVariables.Caption_2_Bold }}>
            {requestInfo.request_type?.toLowerCase().replace(/_/g, " ")}
          </span>
        </Typography>

        <Typography sx={TextVariables.Body_4}>
          State:{" "}
          <span style={{ fontFamily: TextVariables.Caption_2_Bold }}>
            {requestInfo.state?.toLowerCase()}
          </span>
        </Typography>

        <Typography sx={TextVariables.Body_4}>
          Server:{" "}
          <span style={{ fontFamily: TextVariables.Caption_2_Bold }}>
            {requestInfo.request?.platform}
          </span>
        </Typography>

        <Typography sx={TextVariables.Body_4}>
          Account:{" "}
          <span style={{ fontFamily: TextVariables.Caption_2_Bold }}>
            {requestInfo.request?.signal}
          </span>
        </Typography>

        <Typography sx={TextVariables.Body_4}>
          Performance Fee:{" "}
          <span style={{ fontFamily: TextVariables.Caption_2_Bold }}>
            {requestInfo.request?.performance_fee}%
          </span>
        </Typography>
      </Box>
    );
  }
  if (requestType === "IB_DEAL_TREE_CREATE") {
    return <DealTreeModal requestInfo={requestInfo} formattedDate={formattedDate} />;
  }

  return (
    <Box
      sx={{
        backgroundColor: "#EEF2F6",
        borderRadius: "8px",
        padding: "32px 48px",
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <Typography sx={{ fontFamily: TextVariables.Subtitle_4 }}>
        Request details
      </Typography>

      <Typography sx={TextVariables.Body_4}>
        ID:{" "}
        <span style={{ fontFamily: TextVariables.Caption_2_Bold }}>
          {requestInfo.id}
        </span>
      </Typography>

      <Typography sx={TextVariables.Body_4}>
        Request time:{" "}
        <span style={{ fontFamily: TextVariables.Caption_2_Bold }}>
          {formattedDate}
        </span>
      </Typography>

      <Typography sx={TextVariables.Body_4}>
        Request type:{" "}
        <span style={{ fontFamily: TextVariables.Caption_2_Bold }}>
          {requestInfo.request_type?.toLowerCase().replace(/_/g, " ")}
        </span>
      </Typography>

      <Typography sx={TextVariables.Body_4}>
        State:{" "}
        <span style={{ fontFamily: TextVariables.Caption_2_Bold }}>
          {requestInfo.state?.toLowerCase()}
        </span>
      </Typography>

      <Typography sx={TextVariables.Body_4}>
        Broker email:{" "}
        <span style={{ fontFamily: TextVariables.Caption_2_Bold }}>
          {requestInfo.broker_email}
        </span>
      </Typography>
    </Box>
  );
};

  const handleOpenChat = (idChat) => {
    navigate(`/requests/chat/${idChat}`);
  };

  const columns = [
    {
      field: "request_type",
      headerClassName: "header_table",
      renderHeader: () => (
        <TableToolTip title="Order identification number">
          <Box>
            {"Request Type"}
            <IconComponent icon="help" size="12px" />
          </Box>
        </TableToolTip>
      ),
    },
    {
      field: "time_elapsed",
      headerClassName: "header_table",
      renderHeader: () => <>{"Time elapsed"}</>,
    },
    {
      field: "status",
      headerClassName: "header_table",
      renderHeader: () => <>{"Status"}</>,
      renderCell: (params) => {
        if (params.row.status.toLowerCase().includes("aproved")) {
          return <Chip label={params.row.status} sx={styles.ChipAprovedStyle} />;
        } else if (params.row.status.toLowerCase().includes("rejected")) {
          return (
            <Chip
              label={params.row.status.toLowerCase()}
              sx={styles.ChipRejectedStyle}
            />
          );
        } else {
          return (
            <Chip
              label={params.row.status.toLowerCase()}
              sx={styles.ChipPendingStyle}
            />
          );
        }
      },
    },
    {
      field: "chat_history",
      headerClassName: "header_table",
      renderHeader: () => <>&nbsp;&nbsp;&nbsp;&nbsp;Chat history</>,
      renderCell: (params) => {
        return (
          <Box sx={styles.chatMainContainer}>
            <Button
              sx={styles.chatButtonContainer}
              onClick={() => setIdChat(params.row.chat_link)}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: "12px", marginRight: "2px" }}
              >
                chat_paste_go
              </span>
              {params.row.view === "pending" && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "5px",
                  }}
                >
                  <Typography sx={{ fontFamily: TextVariables.Caption_2_Bold }}>
                    View Chat
                  </Typography>
                  <Box>
                    <Box sx={styles.noReadIcon}></Box>
                  </Box>
                </Box>
              )}
              {params.row.view === "view" && (
                <Box>
                  <Typography sx={{ fontFamily: TextVariables.Caption_2_Bold }}>
                    View Chat
                  </Typography>
                </Box>
              )}
              {params.row.view === "none" && (
                <Box>
                  <Typography sx={{ fontFamily: TextVariables.Caption_2_Bold }}>
                    New Chat
                  </Typography>
                </Box>
              )}
            </Button>
          </Box>
        );
      },
    },
    {
      field: "action",
      headerClassName: "header_table",
      sortable: false,
      filterable: false,
      hideable: false,
      resizable: false,
      disableColumnMenu: true,
      hideable: false,
      redimensionable: false,
      renderHeader: () => <>{"View more"}</>,
      renderCell: (params) => {
        return (
          <Box
            variant="contained"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              height: "100%",
              padding: 0,
              minWidth: "auto",
              width: "auto",
            }}
          >
            <IconButton onClick={(e) => handleViewMore(params.row.chat_link)}>
              <img src={actionLogo} alt="Logo" style={{ width: 16, height: 16 }} />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={mainBox}>
      <Modal
        open={viewModal}
        onClose={() => {
          setViewModal(false);
        }}
      >
        <Box sx={styles.modalView}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: Spaces.Size_7,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <Typography
                sx={{
                  padding: "0px 0 0 10px",
                  fontFamily: TextVariables.Subtitle_4,
                }}
              >
                Request details
              </Typography>
              <Button
                onClick={() => {
                  setViewModal(false);
                }}
                style={{
                  backgroundColor: "transparent",
                  color: "#000",
                  fontSize: 20,
                  fontWeight: "bold",
                  padding: "0px",
                  minWidth: "0px",
                }}
              >
                <span className="material-symbols-outlined" fontSize="24px">
                  close
                </span>
              </Button>
            </Box>

            <Box sx={styles.modalScrollContainer}>
              <Box sx={styles.modalContainer}>{viewModal && renderComponent()}</Box>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Paper elevation={2} sx={styles.mainPaper}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "left",
          }}
        >
          <Typography sx={TextVariables.Subtitle_4}>Requests</Typography>
          <Typography sx={TextVariables.Body_4}>
            Manage your request and keep yourself updated with their status.
          </Typography>
        </Box>
        <Box 
          sx={{ 
            width: "100%",
            position: "relative",
            zIndex: 2, 
          }}
        >
          <Grid container spacing={3}>
            {dataGrid.map((group, index) => {
              const visibleItems = group.items.filter(item =>
                item.filter.includes(type)
              );

              if (!visibleItems.length) return null;

              const isExpanded = expanded === index;

              return (
                <Grid item xs={12} md={4} key={index} sx={{ position: "relative", overflow: "visible",}}>
                  <Accordion 
                    expanded={isExpanded} 
                    onChange={() =>
                      setExpanded(isExpanded ? null : index)
                    }
                    elevation={2}
                    sx={{
                      borderRadius: "12px",
                      overflow: "visible",
                      "&:before": {
                          display: "none",
                      },
                    }}
                  >
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        minHeight: 70,
                        "& .MuiAccordionSummary-content": {
                            margin: "12px 0",
                        },
                      }}
                    >
                      <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                        }}
                      >
                        <IconComponent icon={group.icon} />
                        <Typography sx={{ fontWeight: 600}}>
                          {group.title}
                        </Typography>
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails 
                      sx={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        width: "100%",
                        backgroundColor: "#FFF",
                        borderRadius: "0 0 12px 12px",
                        boxShadow: "0px 10px 25px rgba(0,0,0,.15)",
                        zIndex: 1000,
                        p: 0,
                      }}
                    >
                        <List disablePadding>
                          {visibleItems.map((item, idx) => (
                            <ListItemButton
                              key={idx}
                              onClick={() => navigate(item.linkTo)}
                              sx={{
                                py: 2,
                                borderTop: idx !== 0 ? "1px solid #eee" : "none",
                                "&:hover": { background: "#F8F8F8" },
                              }}
                            >
                                <ListItemIcon>
                                  <IconComponent icon={item.icon} />
                                </ListItemIcon>

                                <ListItemText
                                  primary={item.title}
                                />

                                <span className="material-symbols-outlined">
                                  chevron_right
                                </span>
                            </ListItemButton>
                          ))}
                        </List>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              );
            })}
          </Grid>
        </Box>
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            height: 602,
            width: "100%",
            gap: Spaces.Size_2,
          }}
        >
          <Typography sx={TextVariables.Body_4_Bold}>Requests status</Typography>
          <TableComponent rows={rows} columns={columns} />
        </Box>
      </Paper>
    </Box>
  );
}

export default BackofficePage;
